import React from "react";
import { Badge } from "@/components/ui/badge";

/**
 * @typedef {Object} ConfidenceThresholds
 * @property {number} high - minimum value (0-100) considered high confidence
 * @property {number} mid - minimum value (0-100) considered medium confidence
 */

/**
 * @typedef {Object} ConfidenceBadgeProps
 * @property {number} value - confidence percentage, 0-100
 * @property {'bar'|'badge'} [variant]
 * @property {ConfidenceThresholds} [thresholds]
 * @property {string} [className]
 */

const DEFAULT_THRESHOLDS = { high: 85, mid: 60 };

function band(value, thresholds) {
  if (value >= thresholds.high) return { key: "high", bar: "bg-emerald-500", text: "text-emerald-700", label: "High confidence" };
  if (value >= thresholds.mid) return { key: "medium", bar: "bg-amber-500", text: "text-amber-700", label: "Medium confidence" };
  return { key: "low", bar: "bg-rose-500", text: "text-rose-700", label: "Low confidence" };
}

/**
 * Confidence indicator. Always pairs color with a text label — the original
 * inline implementation (AISIngestion.jsx) was color-only, a real a11y gap
 * (color can't be the only way information is conveyed). Fixed here.
 * @param {ConfidenceBadgeProps} props
 */
export default function ConfidenceBadge({ value, variant = "bar", thresholds = DEFAULT_THRESHOLDS, className = "" }) {
  const b = band(value, thresholds);

  if (variant === "badge") {
    return (
      <Badge variant="outline" className={`text-[10px] ${b.text} border-current/30 ${className}`}>
        {value}% · {b.label}
      </Badge>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <span className={`text-[10px] font-medium ${b.text}`}>{value}% · {b.label}</span>
      <div className={`h-1.5 w-10 rounded-full ${b.bar}`} aria-hidden="true" />
    </div>
  );
}
